import { memo, useLayoutEffect, useMemo, useRef } from 'react'
import { timeCursor } from '../../app/time/cursor'
import { WxIcon } from '../icons'
import type { ZoneProps } from '../data/types'

const HOURS = 25

/** Apple-grade hourly strip: one row, scrolls sideways, an hour tap moves the whole page's time. */
export const Hourly = memo(function Hourly({ data }: ZoneProps) {
  const ref = useRef<HTMLDivElement>(null)
  const hours = useMemo(() => data.models?.hours.slice(0, HOURS) ?? [], [data.models])
  const summary = data.report?.summaries.hourly || data.now?.summary || ''
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const sync = () => {
      const at = timeCursor.isNow() ? null : data.models?.indexAt(timeCursor.instant()) ?? null
      node.querySelectorAll<HTMLElement>('[data-hour]').forEach((cell, i) => {
        const on = at == null ? i === 0 : i === at
        if (cell.getAttribute('aria-pressed') !== String(on)) cell.setAttribute('aria-pressed', String(on))
      })
    }
    // The edge fade says "more to the right" until the strip reaches its end.
    const edge = () => node.toggleAttribute('data-end', node.scrollLeft + node.clientWidth >= node.scrollWidth - 2)
    sync(); edge()
    node.addEventListener('scroll', edge, { passive: true })
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(edge)
    resize?.observe(node)
    const off = timeCursor.subscribe(sync)
    return () => { off(); resize?.disconnect(); node.removeEventListener('scroll', edge) }
  }, [data.models, hours])
  const pick = (index: number, t: number) => {
    if (index === 0) timeCursor.toNow(); else timeCursor.set(t)
    timeCursor.flush()
  }
  return <section className="v7-card v7-hourly" data-qa="hourly" aria-labelledby="v7-hourly-title">
    <header className="v7-card-head">
      <h2 id="v7-hourly-title">Hourly forecast</h2>
      {summary ? <p className="v7-card-summary">{summary}</p> : null}
    </header>
    <div className="v7-hour-row" ref={ref} role="list">
      {hours.length ? hours.map((hour, i) => <div role="listitem" key={hour.t}>
        <button type="button" data-hour={i} aria-pressed={i === 0} onClick={() => pick(i, hour.t)}
          aria-label={`${i === 0 ? 'Now' : hour.time}: ${hour.temp ?? 'unavailable'}, ${hour.conditionText}${hour.chanceValue != null && hour.chanceValue >= 20 ? `, ${hour.chance} chance of precipitation` : ''}`}>
          <span className="v7-hour-time">{i === 0 ? 'Now' : hour.time}</span>
          <span className="v7-hour-icon"><WxIcon condition={hour.condition} night={!hour.isDay} size={28} label={null} /></span>
          <span className="v7-hour-chance">{hour.chanceValue != null && hour.chanceValue >= 20 ? hour.chance : ''}</span>
          <strong className="v7-hour-temp">{hour.temp ?? '—'}</strong>
        </button>
      </div>) : Array.from({ length: 12 }, (_, i) => <div role="listitem" key={i}><span className="v7-hour-skeleton" /></div>)}
    </div>
  </section>
})
